import { ThemeProvider } from '@emotion/react';
import { useEffect, Fragment } from 'react';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Clamp from 'react-multiline-clamp';
import { createTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getBackstageByUsername, getUsersBackstage } from 'APIs/BackstageApi';
import { useAppSelector } from 'app/hooks';
import ProfileOtherPostCardWithData from 'components/molecules/Card/ProfileOtherPostCardWithData';
import ProfileOwnPostCard from 'components/molecules/Card/ProfileOwnPostCard';
import EditBackstageDescModal from 'components/organisms/EditBackstageDescModal';
import { selectAuth } from 'features/auth/authSlice';
import Backstage from 'interfaces/Backstage.interface';
import { UserData } from 'interfaces/UserData.interface';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BackstageProfileCard from 'components/molecules/Card/BackstageProfileCard';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import { Alert, Stack } from '@mui/material';
import Loader from 'components/atoms/loader';
import { NormalButton } from 'components/atoms/Button';
import {
  checkSub,
  handleToggleSubscribe,
  handleTrialSubscribe,
} from 'APIs/utils';
import { useDispatch } from 'react-redux';
import { getEthUsdRates } from 'APIs/transactionApi';
import { verifyBackstageToken } from 'APIs/UserDataApi';
import SubscribeModal from 'components/molecules/SubscribeModal';
import BackstagePrompt from 'views/DesktopViews/OtherProfilePage/ProfilePageBody/BackstagePrompt';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const imgStyles = {
  width: '30px',
  height: '30px',
  margin: '0px 5px',
} as React.CSSProperties;

const Body = styled(Grid)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  padding: '4rem',
  backgroundColor: 'rgba(255,255,255,0.1)',
  borderRadius: '20px',
  gap: '6px',
  color: '#fff',
}));

const ContentGrid = styled(Grid)(({ theme }) => ({
  paddingLeft: '44px',
  paddingRight: '44px',
  display: 'flex',
  gap: '6px',
}));

const EditLayer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '3px',
  color: '#ddb930',
  cursor: 'pointer',
  marginBottom: '2px',
  position: 'absolute',
  right: '-40px',
  top: '5px',
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 'bold',
  paddingRight: '10px',
  textAlign: 'center',
  width: '100%',
  position: 'relative',
}));

const EditText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#ddb930',
}));

interface IFeedBody {
  user?: Partial<UserData>;
}

const defaultDesc =
  'This is where you post your description about what you offer in your backstage subscription!';

const FeedBody: React.FC<IFeedBody> = ({ user }) => {
  const { username } = useParams();
  const authState = useAppSelector(selectAuth);
  const [data, setData] = React.useState<Backstage[]>([]);
  const [subscribed, setSubscribed] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();
  const [transaction, setTransaction] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [postsLoading, setPostsLoading] = React.useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = React.useState<boolean>(false);
  const [ethPrice, setEthPrice] = React.useState<string>('');
  const [price, setPrice] = React.useState<string>('0');
  const [validToken, setValidToken] = React.useState<boolean>(false);
  const [subLoading, setSubLoading] = React.useState<boolean>(false);
  const [trial, setTrial] = React.useState(user?.isOnTrial || false);
  const etherSvg = require('assets/ethereum.svg');
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const token = location.search.split('token=')[1];
    if (token) {
      const authenticateToken = async () => {
        if (user && user.id) {
          const verifyToken = await verifyBackstageToken(token, user?.id);
          setValidToken(verifyToken.data);
        }
      };
      authenticateToken();
    }
  }, [location.search, user]);

  const fetchData = async () => {
    setPostsLoading(true);
    if (username) {
      let data: Backstage[] | undefined = [];
      if (authState.username === username) {
        data = await getUsersBackstage();
      } else {
        data = await getBackstageByUsername(username);
      }
      if (data) {
        setData(data);
      }
    }
    setPostsLoading(false);
  };
  const addPost = (post: Backstage) => {
    setData(posts => [{ ...post, likedBy: [], comments: [] }, ...posts]);
  };

  useEffect(() => {
    user &&
      setPrice(
        (user?.uniqueUrl === validToken
          ? user.discountedSubscriptionPrice
            ? user.discountedSubscriptionPrice
            : user.subscriptionPrice
            ? user.subscriptionPrice
            : '0'
          : user.subscriptionPrice) || '0'
      );
  }, [user, validToken]);

  useEffect(() => {
    const ethPrice = async () => {
      if (user) {
        const eth = await getEthUsdRates();
        const price = (
          eth.ETH *
          parseFloat(
            (user?.uniqueUrl === validToken
              ? user.discountedSubscriptionPrice
                ? user.discountedSubscriptionPrice
                : user.subscriptionPrice
                ? user.subscriptionPrice
                : '0'
              : user.subscriptionPrice) || '0'
          )
        ).toFixed(6);
        setEthPrice(price);
      }
    };
    ethPrice();
  }, [user, validToken]);

  useEffect(() => {
    !subscribed &&
      checkSub(
        setSubLoading,
        authState,
        setSubscribed,
        setIsSubscribed,
        setTransaction,
        dispatch,
        user,
        false,
        fetchData
      );
    //eslint-disable-next-line
  }, [authState.id, user?.id]);

  useEffect(() => {
    subscribed && setSubLoading(false);
  }, [subscribed]);

  useEffect(() => {
    const subscribersIds = user?.subscribers?.map(
      ({ from }) => from && from.id
    );
    if (authState?.id && subscribersIds?.includes(authState.id)) {
      setSubscribed(true);
      setTrial(false);
    }
    // eslint-disable-next-line
  }, [user, authState]);

  useEffect(() => {
    const fetchPosts = async () => {
      if (authState.username) await fetchData();
    };
    fetchPosts();
    //eslint-disable-next-line
  }, [username, authState.username]);

  useEffect(() => {
    const isTransaction = location.search.includes('transaction');
    if (isTransaction && authState.id) {
      user?.trialDuration && user?.numberOfSales && user.numberOfSales > 0
        ? handleTrialSubscribe(
            authState,
            user,
            setSubscribed,
            user.trialDuration
          )
        : handleToggleSubscribe(
            authState,
            setError,
            setTransaction,
            setLoading,
            setIsSubscribed,
            dispatch,
            false,
            price,
            user
          );
    }
    //eslint-disable-next-line
  }, [authState.id]);

  if (!user?.canPost) return null;

  return (
    <Fragment>
      <ThemeProvider theme={colortheme}>
        <Body
          container
          style={{
            maxWidth: '1000px',
            margin: '80px 0px',
            background: 'rgba(255,255,255,0.01)',
            borderRadius: '20px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <ContentGrid item xs={12}>
            <TitleText>
              👋 Welcome to my Backstage!
              {authState.username === username && (
                <EditLayer>
                  <EditBackstageDescModal userId={user?.id}>
                    <EditOutlined sx={{ fontSize: '20px' }} />
                    <EditText>Edit</EditText>
                  </EditBackstageDescModal>
                </EditLayer>
              )}
            </TitleText>
          </ContentGrid>
          <ContentGrid item xs={12}>
            {user?.backstageDesc && (
              <Typography
                component={'span'}
                sx={{
                  color: '#c1c1c1',
                  fillRule: 'evenodd',
                  textAlign: 'center',
                  width: '100%',
                  marginTop: '20px',
                }}
              >
                <Clamp
                  showMoreElement={({ toggle }: any) => (
                    <span
                      role="button"
                      onClick={toggle}
                      style={{
                        color: '#ddb930',
                        cursor: 'pointer',
                        WebkitUserSelect: 'none',
                      }}
                    >
                      See more
                    </span>
                  )}
                  showLessElement={({ toggle }: any) => (
                    <span
                      role="button"
                      onClick={toggle}
                      style={{
                        color: '#ddb930',
                        cursor: 'pointer',
                        WebkitUserSelect: 'none',
                      }}
                    >
                      See Less
                    </span>
                  )}
                  withTooltip
                  withToggle
                  lines={2}
                >
                  <span>
                    {user.backstageDesc ? user.backstageDesc : defaultDesc}
                  </span>
                </Clamp>
              </Typography>
            )}
          </ContentGrid>
          {authState.username !== username && (
            <>
              {/* <ContentGrid item xs={12} sx={{ paddingTop: '41px' }}>
                <SubText>Subscribe to support {username}</SubText>
              </ContentGrid> */}
              {subLoading ? (
                <Loader
                  size="2"
                  sx={{ width: '100%', margin: '20px 0px', flex: '1' }}
                />
              ) : (
                <ContentGrid
                  item
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                  // onClick={props.Feed}
                >
                  <SubscribeModal
                    isSubscribed={isSubscribed}
                    loading={loading}
                    price={price}
                    setError={setError}
                    setIsSubscribed={setIsSubscribed}
                    setLoading={setLoading}
                    setSubscribed={setSubscribed}
                    setTransaction={setTransaction}
                    transaction={transaction}
                    user={user}
                    subscribed={subscribed}
                  >
                    <NormalButton
                      sx={{
                        width: '100%',
                        maxWidth: '400px',
                        minWidth: '300px',
                        textTransform: 'uppercase',
                        marginTop: '20px',
                      }}
                      color={subscribed ? '' : 'yellow'}
                      text={subscribed ? 'Unsubscribe' : 'Subscribe'}
                    />
                  </SubscribeModal>
                  <BackstagePrompt transaction={transaction} loading={loading}>
                    <span></span>
                  </BackstagePrompt>
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>
                      $
                      {(user?.uniqueUrl === validToken &&
                        user?.discountedSubscriptionPrice) ||
                        user?.subscriptionPrice ||
                        0}
                      /mo or{' '}
                    </span>
                    <img style={imgStyles} src={etherSvg.default} alt="ether" />{' '}
                    {ethPrice}ETH/mo
                  </Typography>
                </ContentGrid>
              )}
              <Stack
                direction={'column'}
                spacing={2}
                sx={{ width: '100%', marginTop: '10px ' }}
              >
                {transaction && (
                  <Alert
                    onClose={() => setTransaction(false)}
                    severity="success"
                  >
                    Thanks for supporting {user?.username}! We're waiting for
                    the blockchain to finish confirming the transaction before
                    we can unlock the content for you. Please check back in
                    about 15 minutes!
                  </Alert>
                )}
                {error && (
                  <Alert onClose={() => setError('')} severity="error">
                    There was an error in following {user?.username} :{' '}
                    {error.split('(')[0]}
                  </Alert>
                )}
              </Stack>
            </>
          )}
          {username !== authState.username && (
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                marginTop: '60px',
                marginBottom: '20px',
              }}
              variant="h5"
            >
              Member Benefits
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '20px',
                  justifyContent: 'center',
                  margin: '40px 0px',
                }}
              >
                <BackstageProfileCard
                  icon={<DescriptionOutlinedIcon />}
                  heading="posts you have access"
                  number={120}
                  extraText="160 Added"
                />
                <BackstageProfileCard
                  icon={<ImageOutlinedIcon />}
                  heading="photos you have access"
                  number={500}
                  extraText="659 Added"
                />
                <BackstageProfileCard
                  icon={<VideocamOutlinedIcon />}
                  heading="videos you have access"
                  number={75}
                  extraText="107 Added"
                />
                <BackstageProfileCard
                  icon={<FavoriteBorderOutlinedIcon />}
                  heading="likes on all posts"
                  number={10000}
                />
              </Box>
            </Typography>
          )}
          {authState.username === username && (
            <ProfileOwnPostCard addPost={addPost} backstagePosts={data} />
          )}
          {postsLoading ? (
            <Loader size="2" sx={{ margin: '40px 0px', width: '100%' }} />
          ) : data.length > 0 &&
            (subscribed ||
              trial ||
              authState.username === username ||
              (user?.uniqueUrl && validToken)) ? (
            <>
              {data.map(backstage => {
                return (
                  <ProfileOtherPostCardWithData
                    key={backstage.id}
                    data={backstage}
                  />
                );
              })}
            </>
          ) : subscribed || trial || (user?.uniqueUrl && validToken) ? (
            <Box
              sx={{ textAlign: 'center', width: '100%', marginBottom: '80px' }}
              mt={5}
            >
              <TitleText>No posts available</TitleText>
            </Box>
          ) : (
            <></>
          )}
        </Body>
      </ThemeProvider>
    </Fragment>
  );
};

export default FeedBody;
